import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TeamColorService {
  /**
   * Golden-angle increment (in degrees) used to spread hues evenly around the HSL color wheel.
   */
  private static readonly GOLDEN_ANGLE_DEGREES = 137.508;

  /**
   * Base saturation and variation range (percent) for background colors to keep them moderately vivid.
   */
  private static readonly BASE_SATURATION_PERCENT = 50;
  private static readonly SATURATION_RANGE_PERCENT = 30;

  /**
   * Base lightness and variation range (percent) for background colors to keep them in a light/pastel band.
   */
  private static readonly BASE_LIGHTNESS_PERCENT = 77;
  private static readonly LIGHTNESS_RANGE_PERCENT = 15;

  /**
   * Text color adjustments and minimums to ensure sufficient contrast against the background.
   */
  private static readonly MIN_TEXT_SATURATION_PERCENT = 28;
  private static readonly TEXT_SATURATION_OFFSET = 18;
  private static readonly MIN_TEXT_LIGHTNESS_PERCENT = 30;
  private static readonly TEXT_LIGHTNESS_OFFSET = 48;

  /**
   * Return each team's specific color and text color.
   * @param teamId
   */
  getColors(teamId: string): { background: string; text: string } {
    const hash = this.hashString(teamId);
    // Multiply the hash by the golden angle and take modulo 360 to get a well-distributed hue.
    const hue = (hash * TeamColorService.GOLDEN_ANGLE_DEGREES) % 360;
    const saturation =
      TeamColorService.BASE_SATURATION_PERCENT + (hash % TeamColorService.SATURATION_RANGE_PERCENT);
    const lightness =
      TeamColorService.BASE_LIGHTNESS_PERCENT + (hash % TeamColorService.LIGHTNESS_RANGE_PERCENT);
    const background = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    const textSaturation = Math.max(
      TeamColorService.MIN_TEXT_SATURATION_PERCENT,
      saturation - TeamColorService.TEXT_SATURATION_OFFSET
    );
    const textLightness = Math.max(
      TeamColorService.MIN_TEXT_LIGHTNESS_PERCENT,
      lightness - TeamColorService.TEXT_LIGHTNESS_OFFSET
    );
    const text = `hsl(${hue}, ${textSaturation}%, ${textLightness}%)`;
    return {background, text};
  }

  /**
   * Convert a string into a "random" positive number using a simple hash function.
   * @param value to be hashed
   * @returns a positive number derived from the input string
   * @private
   */
  private hashString(value: string): number {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
      hash = (hash << 5) - hash + value.charCodeAt(index);
      hash |= 0;
    }
    return Math.abs(hash);
  }
}
